<template>
    <div class="field" :class="fieldClasses">
        <label v-if="label" class="label" :for="theId">
            {{ label }}{{ hideLabelColon ? '' : ':' }}

            <span>&nbsp;</span>
            <span v-if="tooltip" :id="`${theId}-tooltip-info`" v-tooltip="tooltipInfo">
                <fa-icon icon="info-circle" class="configurationProfile__tooltip-icon" />
            </span>
        </label>
        <p v-if="description" class="control-description" v-text="description" />
        <p class="control" :class="controlClass">
            <input
                :id="theId"
                ref="input"
                class="input"
                :class="inputClass"
                :readonly="readonly"
                :type="type"
                :value="modelValue"
                :placeholder="placeholder"
                :disabled="disabled"
                :autocomplete="autocomplete"
                :max="max"
                :step="step"
                :maxlength="maxlength"
                :min="min"
                :maxLengthPackageCount="maxLengthPackageCount"
                :maxLengthNumber="maxLengthNumber"
                @input="input($event, $event.target.value)"
                @keydown="keydown($event)"
                @blur="blur"
                @focus="onFocus"
                @paste="onPaste"
            />
            <fa-icon v-if="icon" :icon="icon" class="is-left" />
            <fa-icon
                v-if="error && !exclamationHide"
                icon="exclamation"
                class="is-right is-danger"
            />
            <fa-icon v-if="success" icon="check-circle" class="is-right is-success" />
            <fa-icon
                v-if="inputTooltip"
                :id="`${theId}-input-info`"
                v-tooltip="inputTooltipInfo"
                icon="info-circle"
                class="is-right info-icon"
            />
        </p>
        <p
            v-if="error && !hideErrorText"
            class="help has-text-danger"
            :class="sendErrorClass"
            v-html="error"
        />
        <p v-if="success && !hideSuccessText" class="help has-text-success" v-html="success" />
    </div>
</template>

<script>
import validator from 'utils/validator';
import Inputmask from 'inputmask';

export default {
    props: {
        readonly: { default: false },
        shortWidth: { default: false },
        form: { default: null },
        validateType: { default: null },
        fieldName: { default: null },
        modelValue: { default: '' },
        focus: { type: Boolean, default: false },
        type: { type: String, default: 'text' },
        label: { type: String, default: '' },
        placeholder: { type: String, default: '' },
        description: { type: String, default: '' },
        icon: { type: [String, Array], default: '' },
        error: { type: String, default: '' },
        success: { type: String, default: '' },
        loading: { type: Boolean, default: false },
        id: { type: String, default: '' },
        formatter: { type: String, default: '' },
        countryPrefix: { type: String, default: '+1' },
        disabled: { type: Boolean, default: false },
        sensitive: { type: Boolean, default: false },
        autocomplete: { type: String, default: '' },
        max: { type: [Number, String] },
        step: { type: [Number, String], default: 'any' },
        maxlength: { type: Number, default: 524288 },
        filter: { type: [String, Boolean], default: false },
        hideErrorText: { type: Boolean, default: false },
        hideSuccessText: { type: Boolean, default: false },
        hideLabelColon: { type: Boolean, default: false },
        disablePlus: { type: Boolean, default: false },
        min: { type: Number },
        maxLengthPackageCount: { type: Number },
        tooltip: { type: [String, Object], default: '' },
        inputTooltip: { type: [String, Object], default: '' },
        maxLengthNumber: { type: Number },
        exclamationHide: { type: Boolean, default: false },
        wrapperClass: { type: String, default: '' },
    },

    emits: ['input', 'update:modelValue', 'keydown', 'paste', 'focus', 'blur'],

    computed: {
        tooltipInfo() {
            if (typeof this.tooltip === 'object') {
                return {
                    placement: 'top',
                    autoHide: false,
                    container: `#${this.theId}-tooltip-info`,
                    popperClass: 'popover-tooltip-info',
                    html: true,
                    ...this.tooltip,
                };
            }

            return {
                content: this.tooltip,
                container: `#${this.theId}-tooltip-info`,
                placement: 'top',
                autoHide: false,
                html: true,
            };
        },

        inputTooltipInfo() {
            if (typeof this.inputTooltip === 'object') {
                return {
                    placement: 'top',
                    autoHide: false,
                    container: `#${this.theId}-input-info`,
                    html: true,
                    ...this.inputTooltip,
                };
            }

            return {
                content: this.inputTooltip,
                container: `#${this.theId}-input-info`,
                placement: 'top',
                autoHide: false,
                html: true,
            };
        },

        /**
         * Returns a list of classNames for the .control element.
         *
         * @return {String}
         */
        controlClass() {
            let classes = [];

            if (this.icon) {
                classes.push('has-icons-left');
            }

            if (this.shortWidth) {
                classes.push('send-input');
            }

            if (this.error) {
                classes.push('has-icons-right');
            }

            if (this.success) {
                classes.push('has-icons-right');
            }

            if (this.loading) {
                classes.push('is-loading');
                classes.push('has-icons-right');
            }

            return classes.join(' ');
        },

        sendErrorClass() {
            return this.shortWidth ? 'send-error' : '';
        },

        /**
         * Returns a list of classNames for the input.
         *
         * @return {String}
         */
        inputClass() {
            let classes = [];

            if (this.$attrs.class) {
                classes = this.$attrs.class.split(' ');
            }

            if (this.error) {
                classes.push('is-danger');
            }

            if (this.success) {
                classes.push('is-success');
            }

            if (this.sensitive) {
                classes.push('inspect-let-ignore');
            }

            if (this.modelValue) {
                classes.push('is-value');
            }

            return classes.join(' ');
        },

        /**
         * Returns the unique ID for the input.
         *
         * @return {String}
         */
        theId() {
            return this.id ? this.id : `form-input-${Math.random().toString(36).substr(2, 9)}`;
        },

        fieldClasses() {
            return this.wrapperClass;
        },
    },

    watch: {
        formatter(formatter) {
            if (formatter) {
                this.initFormatter();
            }
        },

        /**
         * @param {String} country
         *
         * @return {void}
         */
        countryPrefix(country) {
            if (this.formatter) {
                this.initFormatter();
            }
        },
    },

    methods: {
        /**
         * Initializes the formatter.
         *
         * @return {void}
         */
        initFormatter() {
            window.$ = $;

            switch (this.formatter) {
                case 'phoneGB':
                    Inputmask('+44 9999 999999', {}).mask(`#${this.theId}`);
                    break;
                case 'phone':
                    $(`#${this.theId}`).phone({
                        defaultPrefix: this.countryPrefix,
                    });
                    break;
                case 'currency':
                    Inputmask({
                        alias: 'numeric',
                        groupSeparator: ',',
                        digits: 2,
                        digitsOptional: false,
                        prefix: '$',
                        placeholder: '0',
                        rightAlign: false,
                    }).mask(`#${this.theId}`);
                    break;
                case 'smsCode':
                    Inputmask('9 9 9 9 9 9').mask(`#${this.theId}`);
                    break;
                case 'EIN':
                    Inputmask('99-9999999', {
                        jitMasking: true,
                        showMaskOnHover: false,
                    }).mask(`#${this.theId}`);
            }
        },

        /**
         * When input blur event is triggered, call parent if exists.
         *
         * @return {void}
         */
        blur(e) {
            this.$emit('blur', {
                e: e,
                id: this.id,
                key: this.fieldName,
                value: this.modelValue,
            });
        },

        /**
         * If filter set, removes matches within value, otherwise emits value.
         *
         * @param value
         * @return {void}
         */
        input(e, value = '') {
            if (this.disablePlus && value[0] === '+') {
                this.$refs['input'].value = '';

                return;
            }

            if (
                this.maxLengthPackageCount &&
                String(value) > String(this.maxLengthPackageCount).length
            ) {
                this.$refs['input'].value = this.maxLengthPackageCount;
            }

            if (this.maxLengthNumber && value.length > this.maxLengthNumber) {
                this.$refs['input'].value = value = this.$refs['input'].value.slice(
                    0,
                    this.maxLengthNumber
                );
            }

            this.handleInput(e, value);

            if (this.validateType) {
                this.validation(e, value);
            }
        },

        handleInput(e, value) {
            if (this.filter) {
                value = value.replace(new RegExp(this.filter, 'gm'), '');

                this.$refs['input'].value = value;
            }

            this.$emit('input', value);
            this.$emit('update:modelValue', value);
        },

        validation(e, value) {
            let isValid = false;
            let message = '';
            let errors = this.form.errors;
            let existType = false;

            if (!value) {
                errors.clear(this.fieldName);

                return;
            }

            if (this.validateType === 'date') {
                existType = true;
                isValid = validator.isDate(value);

                if (!isValid) {
                    message = 'Invalid Date';
                } else {
                    errors.clear(this.fieldName);
                }
            }

            if (this.validateType === 'number') {
                existType = true;
                isValid = validator.isNumeric(value);

                if (!isValid) {
                    message = 'Invalid Number';
                } else {
                    errors.clear(this.fieldName);
                }
            }

            if (this.validateType === 'url') {
                existType = true;
                isValid = validator.isURL(value, {
                    require_protocol: true,
                });

                if (!isValid) {
                    message = 'Invalid URL. Example: http(s)://******.***';
                } else {
                    errors.clear(this.fieldName);
                }
            }

            if (!isValid && existType) {
                this.form.onFail({
                    [this.fieldName]: message,
                });
            }
        },

        keydown(e) {
            this.$emit('keydown', {
                e: e,
                id: this.id,
                key: this.fieldName,
                value: this.modelValue,
            });
        },

        onPaste(e) {
            this.$emit('paste', e);
        },

        onFocus(e) {
            this.$emit('focus', e);
        },
    },

    mounted() {
        if (this.focus) {
            this.$refs.input.focus();
        }

        if (this.formatter) {
            this.initFormatter();
        }
    },
};
</script>
